import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {
  fetchCounsellorPortalCalendarSlots,
  publishCounsellorPortalCalendarSlots,
  unpublishCounsellorPortalCalendarSlots,
} from "../../services/counsellorPortalApi";
import { useToast } from "../../hook/useToast";

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
};

const PRIMARY = "#2D315F";
const PRIMARY_DARK = "#1F2347";
const BORDER = "#E6E8F0";
const SURFACE = "#F8F9FC";
const TEXT = "#171725";
const MUTED = "#667085";
const SUCCESS = "#157347";
const WARNING = "#B54708";
const DANGER = "#B42318";

const buildSlotKey = (slot) =>
  slot?.slot_id || `${slot?.date}|${slot?.start_time}|${slot?.end_time}`;

const isSlotLocked = (slot) => Boolean(slot?.is_past || slot?.is_booked);

const isSlotLive = (slot) =>
  Boolean(slot?.is_published || slot?.is_available_to_students);

const toSlotPayload = (slot) => ({
  date: slot.date,
  start_time: slot.start_time,
  end_time: slot.end_time,
});

const getApiErrorMessage = (error, fallback) => {
  const fieldErrors = error?.response?.data?.errors;
  const firstFieldError = fieldErrors ? Object.values(fieldErrors).flat()[0] : null;

  return (
    firstFieldError ||
    error?.response?.data?.message ||
    error?.response?.data?.detail ||
    error?.message ||
    fallback
  );
};

const SlotChip = ({ slot, selected, onToggle }) => {
  if (!slot) return null;

  const isPast = Boolean(slot.is_past);
  const isBooked = Boolean(slot.is_booked);
  const isPublished = Boolean(slot.is_published);
  const isStudentVisible = Boolean(slot.is_available_to_students);
  const disabled = isSlotLocked(slot);
  const removalIntent = selected && isSlotLive(slot);

  let palette = {
    bg: "#fff",
    border: BORDER,
    color: TEXT,
    hover: "#F3F5FA",
    shadow: "none",
  };
  let badge = null;

  if (selected && removalIntent) {
    palette = {
      bg: "#FFF4F2",
      border: "#FDA29B",
      color: DANGER,
      hover: "#FFECE8",
      shadow: "0 10px 24px rgba(180, 35, 24, 0.12)",
    };
    badge = { label: "Remove", bg: DANGER, fg: "#fff" };
  } else if (selected) {
    palette = {
      bg: PRIMARY,
      border: PRIMARY,
      color: "#fff",
      hover: PRIMARY_DARK,
      shadow: "0 12px 26px rgba(45, 49, 95, 0.24)",
    };
    badge = { label: "Publish", bg: "#FFA447", fg: "#171725" };
  } else if (isBooked) {
    palette = {
      bg: "#FFF1F0",
      border: "#FFD0CC",
      color: DANGER,
      hover: "#FFF1F0",
      shadow: "none",
    };
    badge = { label: "Booked", bg: "#F04438", fg: "#fff" };
  } else if (isPast) {
    palette = {
      bg: "#F1F2F5",
      border: "#E2E4EA",
      color: "#98A2B3",
      hover: "#F1F2F5",
      shadow: "none",
    };
    badge = { label: "Past", bg: "#D0D5DD", fg: "#475467" };
  } else if (isStudentVisible) {
    palette = {
      bg: "#ECFDF3",
      border: "#ABEFC6",
      color: SUCCESS,
      hover: "#DFF8E9",
      shadow: "none",
    };
    badge = { label: "Live", bg: SUCCESS, fg: "#fff" };
  } else if (isPublished) {
    palette = {
      bg: "#FFF8E8",
      border: "#FEDF89",
      color: WARNING,
      hover: "#FFF3D6",
      shadow: "none",
    };
    badge = { label: "Published", bg: WARNING, fg: "#fff" };
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={() => !disabled && onToggle?.(slot)}
      disabled={disabled}
      aria-pressed={selected}
      sx={{
        position: "relative",
        width: "100%",
        minHeight: 64,
        appearance: "none",
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.color,
        borderRadius: "8px",
        px: 1.25,
        py: 1,
        fontFamily: "inherit",
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "center",
        transition:
          "background 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
        boxShadow: palette.shadow,
        opacity: disabled ? 0.9 : 1,
        "&:hover": disabled
          ? undefined
          : {
              background: palette.hover,
              transform: "translateY(-1px)",
              boxShadow:
                palette.shadow === "none"
                  ? "0 10px 22px rgba(45, 49, 95, 0.08)"
                  : palette.shadow,
            },
        "&:focus-visible": {
          outline: `2px solid ${PRIMARY}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.6,
          minWidth: 0,
        }}
      >
        {disabled ? <LockOutlinedIcon sx={{ fontSize: 14, opacity: 0.85 }} /> : null}
        <Typography
          component="span"
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "inherit",
            lineHeight: 1.25,
          }}
        >
          {slot.time || `${slot.start_time} - ${slot.end_time}`}
        </Typography>
      </Box>

      {badge ? (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            px: 0.7,
            py: 0.15,
            borderRadius: "999px",
            background: badge.bg,
            color: badge.fg,
            fontSize: 9.5,
            fontWeight: 800,
            textTransform: "uppercase",
          }}
        >
          {badge.label}
        </Box>
      ) : null}
    </Box>
  );
};

SlotChip.propTypes = {
  slot: PropTypes.shape({
    slot_id: PropTypes.string,
    date: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    time: PropTypes.string,
    is_past: PropTypes.bool,
    is_booked: PropTypes.bool,
    is_published: PropTypes.bool,
    is_available_to_students: PropTypes.bool,
  }).isRequired,
  selected: PropTypes.bool,
  onToggle: PropTypes.func,
};

const DayHeader = ({ day, selected, isToday, selectedCount, onClick }) => {
  const formatted = dayjs(day.date);
  const liveCount = day.available_to_students_count || 0;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        width: "100%",
        appearance: "none",
        border: `1px solid ${selected ? PRIMARY : BORDER}`,
        cursor: "pointer",
        fontFamily: "inherit",
        borderRadius: "8px",
        background: selected ? PRIMARY : "#fff",
        color: selected ? "#fff" : TEXT,
        px: 1.25,
        py: 1.1,
        textAlign: "left",
        transition: "background 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
        boxShadow: selected ? "0 12px 24px rgba(45, 49, 95, 0.18)" : "none",
        "&:hover": {
          background: selected ? PRIMARY_DARK : "#F3F5FA",
          borderColor: selected ? PRIMARY_DARK : "#D6DBE8",
        },
        "&:focus-visible": {
          outline: `2px solid ${PRIMARY}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: selected ? "rgba(255,255,255,0.82)" : isToday ? WARNING : MUTED,
              textTransform: "uppercase",
            }}
          >
            {day.weekday?.slice(0, 3) || formatted.format("ddd")}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: "inherit", mt: 0.2 }}>
            {formatted.format("DD MMM")}
          </Typography>
        </Box>

        {selectedCount > 0 ? (
          <Box
            sx={{
              alignSelf: "flex-start",
              minWidth: 24,
              height: 24,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.75,
              bgcolor: selected ? "#FFA447" : PRIMARY,
              color: selected ? "#171725" : "#fff",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {selectedCount}
          </Box>
        ) : null}
      </Box>

      <Typography
        sx={{
          mt: 0.7,
          fontSize: 12,
          fontWeight: 700,
          color: selected ? "rgba(255,255,255,0.78)" : MUTED,
        }}
      >
        {liveCount} live slot{liveCount === 1 ? "" : "s"}
      </Typography>
    </Box>
  );
};

DayHeader.propTypes = {
  day: PropTypes.shape({
    date: PropTypes.string.isRequired,
    label: PropTypes.string,
    weekday: PropTypes.string,
    available_to_students_count: PropTypes.number,
  }).isRequired,
  selected: PropTypes.bool,
  isToday: PropTypes.bool,
  selectedCount: PropTypes.number,
  onClick: PropTypes.func,
};

const CalendarSection = ({
  fetchCalendarSlots = fetchCounsellorPortalCalendarSlots,
  publishCalendarSlots = publishCounsellorPortalCalendarSlots,
  unpublishCalendarSlots = unpublishCounsellorPortalCalendarSlots,
  title = "Publish Your Available Slots",
}) => {
  const { showToast } = useToast();
  const timezone = useMemo(detectTimezone, []);

  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [weekPayload, setWeekPayload] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlotKeys, setSelectedSlotKeys] = useState(() => new Set());
  const requestIdRef = useRef(0);

  const days = weekPayload?.days || [];
  const activeDay =
    days.find((day) => day.date === selectedDate) || days[0] || null;
  const busy = loading || publishing || unpublishing;

  const applyCalendarPayload = (payload) => {
    const safePayload = payload || { days: [] };
    const payloadDays = safePayload.days || [];
    setWeekPayload(safePayload);
    setSelectedDate((currentDate) => {
      if (currentDate && payloadDays.some((day) => day.date === currentDate)) {
        return currentDate;
      }

      return payloadDays[0]?.date || "";
    });
  };

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    fetchCalendarSlots({ days: 7, timezone })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        applyCalendarPayload(response?.data?.data || {});
      })
      .catch((error) => {
        if (requestId !== requestIdRef.current) return;
        showToast(
          getApiErrorMessage(error, "Could not load calendar. Please try again."),
          "error",
        );
        applyCalendarPayload({ days: [] });
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      });
  }, [fetchCalendarSlots, showToast, timezone]);

  const selectedSlots = useMemo(
    () =>
      days
        .flatMap((day) => day.slots || [])
        .filter((slot) => selectedSlotKeys.has(buildSlotKey(slot))),
    [days, selectedSlotKeys],
  );

  const publishableSlots = useMemo(
    () =>
      selectedSlots.filter(
        (slot) => !isSlotLocked(slot) && !isSlotLive(slot),
      ),
    [selectedSlots],
  );

  const unpublishableSlots = useMemo(
    () => selectedSlots.filter((slot) => !isSlotLocked(slot) && isSlotLive(slot)),
    [selectedSlots],
  );

  const selectedCountByDate = useMemo(() => {
    const counts = {};
    selectedSlots.forEach((slot) => {
      counts[slot.date] = (counts[slot.date] || 0) + 1;
    });
    return counts;
  }, [selectedSlots]);

  const totalSlots = useMemo(
    () => days.reduce((count, day) => count + (day.slots?.length || 0), 0),
    [days],
  );
  const liveSlots = useMemo(
    () =>
      days.reduce(
        (count, day) => count + (day.available_to_students_count || 0),
        0,
      ),
    [days],
  );

  const handleDayClick = (date) => {
    setSelectedDate(date);
  };

  const handleSlotToggle = (slot) => {
    if (isSlotLocked(slot)) return;

    const key = buildSlotKey(slot);
    setSelectedSlotKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedSlotKeys(new Set());
  };

  const handlePublish = () => {
    if (!publishableSlots.length) return;

    setPublishing(true);
    publishCalendarSlots({
      slots: publishableSlots.map(toSlotPayload),
      startDate: weekPayload?.start_date || "",
      days: weekPayload?.days?.length || 7,
      timezone,
      replaceExisting: false,
    })
      .then((response) => {
        const count = publishableSlots.length;
        showToast(
          response?.data?.message ||
            `Published ${count} slot${count === 1 ? "" : "s"} for students.`,
          "success",
        );
        applyCalendarPayload(response?.data?.data || {});
        setSelectedSlotKeys(new Set());
      })
      .catch((error) => {
        showToast(
          getApiErrorMessage(error, "Could not publish slots. Please try again."),
          "error",
        );
      })
      .finally(() => setPublishing(false));
  };

  const handleUnpublish = () => {
    if (!unpublishableSlots.length) return;

    setUnpublishing(true);
    unpublishCalendarSlots({
      slots: unpublishableSlots.map(toSlotPayload),
      startDate: weekPayload?.start_date || "",
      days: weekPayload?.days?.length || 7,
      timezone,
    })
      .then((response) => {
        const count = unpublishableSlots.length;
        showToast(
          response?.data?.message ||
            `Unpublished ${count} slot${count === 1 ? "" : "s"}.`,
          "success",
        );
        applyCalendarPayload(response?.data?.data || {});
        setSelectedSlotKeys(new Set());
      })
      .catch((error) => {
        showToast(
          getApiErrorMessage(error, "Could not unpublish slots. Please try again."),
          "error",
        );
      })
      .finally(() => setUnpublishing(false));
  };

  const weekStartLabel = weekPayload?.start_date
    ? dayjs(weekPayload.start_date).format("DD MMM YYYY")
    : "";
  const weekEndLabel = weekPayload?.end_date
    ? dayjs(weekPayload.end_date).format("DD MMM YYYY")
    : "";
  const selectedCount = selectedSlots.length;

  const renderSlotsForDay = (day) => {
    if (!day?.slots?.length) {
      return (
        <Box
          sx={{
            border: `1px dashed ${BORDER}`,
            borderRadius: "8px",
            py: 5,
            px: 2,
            textAlign: "center",
            color: MUTED,
            fontSize: 13,
            bgcolor: "#fff",
          }}
        >
          No time slots configured for this day.
        </Box>
      );
    }

    return (
      <Grid container spacing={1.25}>
        {day.slots.map((slot) => {
          const key = buildSlotKey(slot);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={key}>
              <SlotChip
                slot={slot}
                selected={selectedSlotKeys.has(key)}
                onToggle={handleSlotToggle}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "calc(100vh - 150px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: { xs: 660, md: "calc(100vh - 162px)" },
          borderRadius: "8px",
          border: `1px solid ${BORDER}`,
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.25,
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: `1px solid ${BORDER}`,
            background: "#fff",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: TEXT }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: MUTED, mt: 0.4 }}>
              {weekStartLabel && weekEndLabel
                ? `${weekStartLabel} to ${weekEndLabel}`
                : "Calendar week"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={
                <EventAvailableOutlinedIcon
                  sx={{ fontSize: 17, color: `${SUCCESS} !important` }}
                />
              }
              label={`${liveSlots} live`}
              sx={{
                height: 34,
                bgcolor: "#ECFDF3",
                color: SUCCESS,
                fontWeight: 800,
                borderRadius: "8px",
                "& .MuiChip-icon": { ml: 1 },
              }}
            />
            <Chip
              label={`${totalSlots} slots`}
              sx={{
                height: 34,
                bgcolor: "#F4F5FA",
                color: PRIMARY,
                fontWeight: 800,
                borderRadius: "8px",
              }}
            />
            {weekPayload?.timezone ? (
              <Chip
                label={weekPayload.timezone}
                sx={{
                  height: 34,
                  bgcolor: "#FFF8E8",
                  color: WARNING,
                  fontWeight: 800,
                  borderRadius: "8px",
                }}
              />
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "300px minmax(0, 1fr)" },
            bgcolor: SURFACE,
          }}
        >
          <Box
            sx={{
              borderRight: { xs: "none", lg: `1px solid ${BORDER}` },
              borderBottom: { xs: `1px solid ${BORDER}`, lg: "none" },
              bgcolor: "#FBFCFE",
              p: { xs: 2, md: 2.5 },
              minHeight: 0,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: TEXT, mb: 1.25 }}>
              Week
            </Typography>

            <Box
              sx={{
                display: { xs: "flex", lg: "grid" },
                gridTemplateColumns: "1fr",
                gap: 1,
                overflowX: { xs: "auto", lg: "visible" },
                overflowY: { xs: "hidden", lg: "auto" },
                pb: { xs: 0.5, lg: 0 },
                pr: { xs: 0, lg: 0.5 },
                maxHeight: { lg: "calc(100vh - 292px)" },
                "&::-webkit-scrollbar": { height: 6, width: 6 },
                "&::-webkit-scrollbar-thumb": {
                  background: "#D8DCE8",
                  borderRadius: 999,
                },
              }}
            >
              {days.map((day) => (
                <Box key={day.date} sx={{ minWidth: { xs: 154, lg: 0 } }}>
                  <DayHeader
                    day={day}
                    selected={day.date === selectedDate}
                    isToday={day.label === "Today"}
                    selectedCount={selectedCountByDate[day.date] || 0}
                    onClick={() => handleDayClick(day.date)}
                  />
                </Box>
              ))}
              {!loading && !days.length ? (
                <Typography sx={{ fontSize: 13, color: MUTED, py: 2 }}>
                  No calendar days available.
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              p: { xs: 2, md: 3 },
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: TEXT }}>
                  {activeDay?.label ||
                    (activeDay?.date
                      ? dayjs(activeDay.date).format("dddd, DD MMM YYYY")
                      : "Slots")}
                </Typography>
                <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.35 }}>
                  {activeDay?.date ? dayjs(activeDay.date).format("dddd, DD MMM YYYY") : ""}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                <Chip
                  size="small"
                  label={`${activeDay?.published_count || 0} published`}
                  sx={{
                    bgcolor: "#FFF8E8",
                    color: WARNING,
                    fontWeight: 800,
                    borderRadius: "8px",
                  }}
                />
                <Chip
                  size="small"
                  label={`${activeDay?.available_to_students_count || 0} live`}
                  sx={{
                    bgcolor: "#ECFDF3",
                    color: SUCCESS,
                    fontWeight: 800,
                    borderRadius: "8px",
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                border: `1px solid ${BORDER}`,
                borderRadius: "8px",
                bgcolor: "#fff",
                p: { xs: 1.5, md: 2 },
                overflow: "auto",
                "&::-webkit-scrollbar": { width: 8 },
                "&::-webkit-scrollbar-thumb": {
                  background: "#D8DCE8",
                  borderRadius: 999,
                },
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    minHeight: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    color: MUTED,
                  }}
                >
                  <CircularProgress size={20} sx={{ color: PRIMARY }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    Loading slots
                  </Typography>
                </Box>
              ) : activeDay ? (
                renderSlotsForDay(activeDay)
              ) : (
                <Typography sx={{ fontSize: 14, color: MUTED, textAlign: "center", py: 6 }}>
                  No slots to display yet.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.8,
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            borderTop: `1px solid ${BORDER}`,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={
                selectedCount > 0
                  ? `${selectedCount} selected`
                  : "No slots selected"
              }
              sx={{
                height: 34,
                bgcolor: selectedCount > 0 ? "#EEF1FB" : "#F4F5FA",
                color: selectedCount > 0 ? PRIMARY : MUTED,
                fontWeight: 800,
                borderRadius: "8px",
              }}
            />
            {selectedCount > 0 ? (
              <Button
                size="small"
                variant="text"
                onClick={handleClearSelection}
                disabled={busy}
                startIcon={<RestoreIcon sx={{ fontSize: 15 }} />}
                sx={{
                  minHeight: 34,
                  textTransform: "none",
                  color: MUTED,
                  fontWeight: 800,
                  borderRadius: "8px",
                  "&:hover": { background: "#F4F5FA" },
                }}
              >
                Clear
              </Button>
            ) : null}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", md: "flex-end" },
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleUnpublish}
              disabled={busy || unpublishableSlots.length === 0}
              startIcon={
                unpublishing ? (
                  <CircularProgress size={15} sx={{ color: DANGER }} />
                ) : (
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                minHeight: 40,
                minWidth: 166,
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#FDA29B",
                color: DANGER,
                fontSize: 14,
                fontWeight: 800,
                "&:hover": {
                  borderColor: DANGER,
                  bgcolor: "#FFF4F2",
                },
                "&.Mui-disabled": {
                  borderColor: "#E4E7EC",
                  color: "#A4A7AE",
                },
              }}
            >
              {unpublishing
                ? "Unpublishing"
                : `Unpublish ${unpublishableSlots.length || ""}`.trim()}
            </Button>

            <Button
              variant="contained"
              onClick={handlePublish}
              disabled={busy || publishableSlots.length === 0}
              startIcon={
                publishing ? (
                  <CircularProgress size={15} sx={{ color: "#fff" }} />
                ) : (
                  <PublishOutlinedIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                minHeight: 40,
                minWidth: 150,
                textTransform: "none",
                bgcolor: PRIMARY,
                color: "#fff",
                borderRadius: "8px",
                px: 2.5,
                fontSize: 14,
                fontWeight: 800,
                boxShadow: "0 12px 24px rgba(45, 49, 95, 0.18)",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 14px 28px rgba(45, 49, 95, 0.24)",
                },
                "&.Mui-disabled": {
                  bgcolor: "#A8ADC4",
                  color: "#fff",
                  boxShadow: "none",
                },
              }}
            >
              {publishing
                ? "Publishing"
                : `Publish ${publishableSlots.length || ""}`.trim()}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

CalendarSection.propTypes = {
  fetchCalendarSlots: PropTypes.func,
  publishCalendarSlots: PropTypes.func,
  unpublishCalendarSlots: PropTypes.func,
  title: PropTypes.string,
};

export default CalendarSection;
